import * as THREE from 'three';
import type { TrackDefinition } from '../core/types';
import type { TrackRuntime, TrackSample } from '../tracks/TrackRuntime';

export interface TrackModel {
  root: THREE.Group;
  roadPoints: THREE.Vector3[];
  boosterMaterials: THREE.MeshStandardMaterial[];
  checkpointGroups: THREE.Group[];
  finishGroup: THREE.Group;
  update(elapsed: number): void;
  dispose(): void;
}

function orientToSample(object: THREE.Object3D, sample: TrackSample): void {
  const matrix = new THREE.Matrix4().makeBasis(sample.right, sample.up, sample.forward);
  object.quaternion.setFromRotationMatrix(matrix);
}

function canvasLabel(text: string, foreground: string, background: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 128;
  const context = canvas.getContext('2d')!;
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.strokeStyle = foreground;
  context.lineWidth = 8;
  context.strokeRect(8, 8, canvas.width - 16, canvas.height - 16);
  context.fillStyle = foreground;
  context.font = '900 48px Arial, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, canvas.width / 2, canvas.height / 2 + 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  return texture;
}

function createGate(
  sample: TrackSample,
  width: number,
  label: string,
  accent: number,
  secondary: number,
): THREE.Group {
  const group = new THREE.Group();
  const dark = new THREE.MeshStandardMaterial({ color: 0x111822, roughness: 0.3, metalness: 0.75 });
  const glow = new THREE.MeshStandardMaterial({ color: accent, emissive: accent, emissiveIntensity: 2.6, roughness: 0.25, metalness: 0.5 });
  const other = new THREE.MeshStandardMaterial({ color: secondary, emissive: secondary, emissiveIntensity: 1.5, roughness: 0.3 });
  // The chase camera rides roughly five metres above the road at high speed;
  // keep every gate comfortably above that envelope so it never cuts through
  // the header while the car passes underneath.
  const gateHeaderY = 6.25;
  const pillarGeometry = new THREE.BoxGeometry(0.48, 6.6, 0.48);
  for (const side of [-1, 1]) {
    const pillar = new THREE.Mesh(pillarGeometry, dark);
    pillar.position.set(side * width * 0.47, 3.2, 0);
    pillar.castShadow = true;
    group.add(pillar);
    const strip = new THREE.Mesh(new THREE.BoxGeometry(0.12, 5.9, 0.51), side > 0 ? glow : other);
    strip.position.set(side * width * 0.47, 3.15, 0.02);
    group.add(strip);
  }
  const header = new THREE.Mesh(new THREE.BoxGeometry(width, 0.72, 0.56), dark);
  header.position.y = gateHeaderY;
  header.castShadow = true;
  group.add(header);
  const sign = new THREE.Mesh(
    new THREE.PlaneGeometry(Math.min(width * 0.68, 8), 1.45),
    new THREE.MeshBasicMaterial({
      map: canvasLabel(label, '#f4fbff', `#${accent.toString(16).padStart(6, '0')}`),
      transparent: false,
      side: THREE.DoubleSide,
    }),
  );
  sign.name = 'gate-label';
  // Drivers approach from local -Z. Keep the label on that face of the
  // header so the solid header beam cannot depth-occlude it.
  sign.position.set(0, gateHeaderY, -0.3);
  sign.rotation.y = Math.PI;
  group.add(sign);
  group.position.copy(sample.position);
  orientToSample(group, sample);
  return group;
}

function createStartFinish(runtime: TrackRuntime, track: TrackDefinition): { start: THREE.Group; finish: THREE.Group } {
  const startSample = runtime.sampleAt(0);
  const finishSample = runtime.sampleAt(1);
  const start = createGate(startSample, startSample.width, 'START', track.palette.accent, track.palette.secondary);
  const finish = createGate(finishSample, finishSample.width, 'FINISH', track.palette.secondary, track.palette.accent);
  start.name = 'start-gate';
  // Keep the countdown camera and car clear while preserving a visible arch
  // immediately ahead of the launch grid.
  start.position.addScaledVector(startSample.forward, 5.5);
  finish.name = 'finish-gate';

  const checkerMaterial = new THREE.MeshBasicMaterial({ map: createCheckerTexture(), side: THREE.DoubleSide });
  const finishLine = new THREE.Mesh(new THREE.PlaneGeometry(finishSample.width, 2.4), checkerMaterial);
  finishLine.rotation.x = -Math.PI / 2;
  finishLine.position.y = 0.035;
  finishLine.position.z = 0.3;
  finish.add(finishLine);
  return { start, finish };
}

function createCheckerTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const context = canvas.getContext('2d')!;
  const size = 32;
  for (let x = 0; x < 8; x += 1) {
    for (let y = 0; y < 2; y += 1) {
      context.fillStyle = (x + y) % 2 === 0 ? '#f4fbff' : '#101722';
      context.fillRect(x * size, y * size, size, size);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function intervalIntersectsGap(runtime: TrackRuntime, start: number, end: number): boolean {
  const low = Math.min(start, end);
  const high = Math.max(start, end);
  return runtime.jumpRanges.some((jump) => low < jump.gapEnd && high > jump.gapStart);
}

function buildRoadGeometry(runtime: TrackRuntime, track: TrackDefinition, segments: number): { road: THREE.Mesh; points: THREE.Vector3[] } {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const points: THREE.Vector3[] = [];
  for (let index = 0; index <= segments; index += 1) {
    const progress = index / segments;
    const sample = runtime.sampleAt(progress);
    points.push(sample.position.clone());
    const halfWidth = sample.width / 2;
    const left = sample.position.clone().addScaledVector(sample.right, -halfWidth);
    const right = sample.position.clone().addScaledVector(sample.right, halfWidth);
    positions.push(left.x, left.y + 0.02, left.z, right.x, right.y + 0.02, right.z);
    normals.push(sample.up.x, sample.up.y, sample.up.z, sample.up.x, sample.up.y, sample.up.z);
    uvs.push(0, sample.distance / 9, 1, sample.distance / 9);
    if (index < segments) {
      const nextProgress = (index + 1) / segments;
      if (intervalIntersectsGap(runtime, progress, nextProgress)) continue;
      const vertex = index * 2;
      indices.push(vertex, vertex + 2, vertex + 1, vertex + 1, vertex + 2, vertex + 3);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  const roadMaterial = new THREE.MeshStandardMaterial({
    color: track.palette.road,
    roughness: 0.72,
    metalness: track.theme === 'neon' || track.theme === 'sky' ? 0.36 : 0.14,
  });
  const road = new THREE.Mesh(geometry, roadMaterial);
  road.name = 'road-surface';
  road.receiveShadow = true;
  return { road, points };
}

function buildEdgeGeometry(runtime: TrackRuntime, track: TrackDefinition, segments: number, side: -1 | 1): THREE.Mesh {
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  for (let index = 0; index <= segments; index += 1) {
    const progress = index / segments;
    const sample = runtime.sampleAt(progress);
    const inner = sample.position.clone().addScaledVector(sample.right, side * (sample.width / 2 - 0.34));
    const outer = sample.position.clone().addScaledVector(sample.right, side * (sample.width / 2 + 0.18));
    positions.push(inner.x, inner.y + 0.05, inner.z, outer.x, outer.y + 0.05, outer.z);
    normals.push(sample.up.x, sample.up.y, sample.up.z, sample.up.x, sample.up.y, sample.up.z);
    if (index < segments) {
      const nextProgress = (index + 1) / segments;
      if (intervalIntersectsGap(runtime, progress, nextProgress)) continue;
      const vertex = index * 2;
      indices.push(vertex, vertex + 2, vertex + 1, vertex + 1, vertex + 2, vertex + 3);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);
  const edgeMaterial = new THREE.MeshStandardMaterial({
    color: side > 0 ? track.palette.accent : track.palette.secondary,
    emissive: side > 0 ? track.palette.accent : track.palette.secondary,
    emissiveIntensity: track.theme === 'neon' || track.theme === 'sky' ? 1.7 : 0.35,
    roughness: 0.38,
  });
  const mesh = new THREE.Mesh(geometry, edgeMaterial);
  mesh.name = side < 0 ? 'road-edge-left' : 'road-edge-right';
  mesh.receiveShadow = true;
  return mesh;
}

function addRoadMarkings(root: THREE.Group, runtime: TrackRuntime, track: TrackDefinition): void {
  const count = Math.floor(runtime.totalLength / 12);
  const geometry = new THREE.BoxGeometry(0.16, 0.035, 4.8);
  const markingMaterial = new THREE.MeshStandardMaterial({
    color: 0xf4f7ec,
    emissive: track.theme === 'neon' ? 0x7aaeff : 0x000000,
    emissiveIntensity: track.theme === 'neon' ? 1.4 : 0,
    roughness: 0.55,
  });
  const samples: TrackSample[] = [];
  for (let index = 0; index < count; index += 1) {
    const distance = index * 12 + 6;
    const halfLengthProgress = 2.4 / runtime.totalLength;
    const progress = distance / runtime.totalLength;
    if (intervalIntersectsGap(runtime, progress - halfLengthProgress, progress + halfLengthProgress)) {
      continue;
    }
    samples.push(runtime.sampleDistance(distance));
  }
  const markings = new THREE.InstancedMesh(geometry, markingMaterial, samples.length);
  const dummy = new THREE.Object3D();
  samples.forEach((sample, index) => {
    dummy.position.copy(sample.position).addScaledVector(sample.up, 0.06);
    orientToSample(dummy, sample);
    dummy.updateMatrix();
    markings.setMatrixAt(index, dummy.matrix);
  });
  markings.name = 'center-markings';
  markings.receiveShadow = true;
  root.add(markings);
}

function addBarriers(root: THREE.Group, runtime: TrackRuntime, track: TrackDefinition): void {
  const spacing = 5.5;
  const count = Math.floor(runtime.totalLength / spacing);
  const baseGeometry = new THREE.BoxGeometry(0.24, 0.8, spacing * 1.03);
  const railMaterial = new THREE.MeshStandardMaterial({ color: 0xc9d2da, roughness: 0.35, metalness: 0.72 });
  const accentMaterial = new THREE.MeshStandardMaterial({
    color: track.palette.accent,
    emissive: track.palette.accent,
    emissiveIntensity: track.theme === 'neon' || track.theme === 'sky' ? 2.2 : 0.35,
    roughness: 0.25,
  });
  const entries: Array<{ sample: TrackSample; side: -1 | 1 }> = [];
  for (let index = 0; index < count; index += 1) {
    const progress = (index * spacing) / runtime.totalLength;
    const halfSpacingProgress = spacing * 0.515 / runtime.totalLength;
    if (intervalIntersectsGap(runtime, progress - halfSpacingProgress, progress + halfSpacingProgress)) {
      continue;
    }
    for (const side of [-1, 1] as const) {
      if (runtime.hasBarrierAt(progress, side < 0 ? 'left' : 'right')) entries.push({ sample: runtime.sampleAt(progress), side });
    }
  }
  const rails = new THREE.InstancedMesh(baseGeometry, railMaterial, entries.length);
  const lights = new THREE.InstancedMesh(new THREE.BoxGeometry(0.27, 0.11, spacing * 0.62), accentMaterial, entries.length);
  const dummy = new THREE.Object3D();
  const lightDummy = new THREE.Object3D();
  entries.forEach(({ sample, side }, index) => {
    dummy.position.copy(sample.position).addScaledVector(sample.right, side * (sample.width / 2 + 0.18)).addScaledVector(sample.up, 0.42);
    orientToSample(dummy, sample);
    dummy.updateMatrix();
    rails.setMatrixAt(index, dummy.matrix);
    lightDummy.position.copy(dummy.position).addScaledVector(sample.up, 0.22).addScaledVector(sample.right, -side * 0.02);
    lightDummy.quaternion.copy(dummy.quaternion);
    lightDummy.updateMatrix();
    lights.setMatrixAt(index, lightDummy.matrix);
  });
  rails.castShadow = true;
  rails.receiveShadow = true;
  root.add(rails, lights);
}

function createBooster(sample: TrackSample, track: TrackDefinition): { group: THREE.Group; material: THREE.MeshStandardMaterial } {
  const group = new THREE.Group();
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(sample.width * 0.8, 0.06, 5.5),
    new THREE.MeshStandardMaterial({ color: 0x132332, roughness: 0.28, metalness: 0.75 }),
  );
  base.receiveShadow = true;
  group.add(base);
  const glow = new THREE.MeshStandardMaterial({
    color: track.palette.accent,
    emissive: track.palette.accent,
    emissiveIntensity: 4,
    roughness: 0.2,
    metalness: 0.32,
  });
  for (let index = -3; index <= 3; index += 1) {
    const strip = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.08, 4.9), glow);
    strip.position.set(index * (sample.width * 0.09), 0.07, 0);
    group.add(strip);
  }
  group.position.copy(sample.position).addScaledVector(sample.up, 0.075);
  orientToSample(group, sample);
  return { group, material: glow };
}

function createArrowSign(sample: TrackSample, track: TrackDefinition, side: -1 | 1): THREE.Group {
  const group = new THREE.Group();
  group.name = 'direction-sign';
  group.userData.progress = sample.progress;
  const post = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.12, 2.4, 6),
    new THREE.MeshStandardMaterial({ color: 0x76828d, roughness: 0.4, metalness: 0.7 }),
  );
  post.position.y = 1.2;
  post.castShadow = true;
  group.add(post);
  const shape = new THREE.Shape();
  // An upward arrow on a track-facing sign is read as "continue forward";
  // using local Y avoids accidentally pointing across the road.
  shape.moveTo(-0.48, -1.1);
  shape.lineTo(-0.48, 0.25);
  shape.lineTo(-0.9, 0.25);
  shape.lineTo(0, 1.15);
  shape.lineTo(0.9, 0.25);
  shape.lineTo(0.48, 0.25);
  shape.lineTo(0.48, -1.1);
  shape.closePath();
  const arrow = new THREE.Mesh(
    new THREE.ExtrudeGeometry(shape, { depth: 0.08, bevelEnabled: false }),
    new THREE.MeshStandardMaterial({ color: track.palette.accent, emissive: track.palette.accent, emissiveIntensity: 1.8 }),
  );
  arrow.name = 'direction-arrow';
  arrow.position.set(-0.04, 2.75, 0);
  group.add(arrow);
  group.position.copy(sample.position).addScaledVector(sample.right, side * (sample.width / 2 + 2.2));
  orientToSample(group, sample);
  // Every sign faces oncoming traffic regardless of which shoulder it uses.
  group.rotateY(Math.PI);
  return group;
}

function addTunnels(root: THREE.Group, runtime: TrackRuntime, track: TrackDefinition): void {
  const stride = 7;
  const count = Math.floor(runtime.totalLength / stride);
  const shellMaterial = new THREE.MeshStandardMaterial({ color: 0x171d2b, roughness: 0.48, metalness: 0.55, side: THREE.DoubleSide });
  const lightMaterial = new THREE.MeshStandardMaterial({ color: track.palette.secondary, emissive: track.palette.secondary, emissiveIntensity: 3.8 });
  for (let index = 0; index < count; index += 1) {
    const progress = (index * stride) / runtime.totalLength;
    if (!runtime.isTunnelAt(progress)) continue;
    const sample = runtime.sampleAt(progress);
    const group = new THREE.Group();
    const roof = new THREE.Mesh(new THREE.BoxGeometry(sample.width + 2.6, 0.28, stride * 1.04), shellMaterial);
    roof.position.y = 5.2;
    const left = new THREE.Mesh(new THREE.BoxGeometry(0.32, 5.2, stride * 1.04), shellMaterial);
    left.position.set(-sample.width / 2 - 1.15, 2.6, 0);
    const right = left.clone();
    right.position.x *= -1;
    const light = new THREE.Mesh(new THREE.BoxGeometry(sample.width * 0.56, 0.08, 1.2), lightMaterial);
    light.position.y = 5.02;
    group.add(roof, left, right, light);
    group.position.copy(sample.position);
    orientToSample(group, sample);
    root.add(group);
  }
}

export function createTrackModel(runtime: TrackRuntime): TrackModel {
  const track = runtime.definition;
  const root = new THREE.Group();
  root.name = `track-${track.id}`;
  const segments = Math.min(1100, Math.max(260, Math.ceil(runtime.totalLength / 2.2)));
  const { road, points } = buildRoadGeometry(runtime, track, segments);
  root.add(road, buildEdgeGeometry(runtime, track, segments, -1), buildEdgeGeometry(runtime, track, segments, 1));
  addRoadMarkings(root, runtime, track);
  addBarriers(root, runtime, track);
  addTunnels(root, runtime, track);

  const checkpointGroups = runtime.checkpointProgresses.map((progress, index) => {
    const sample = runtime.sampleAt(progress);
    const gate = createGate(sample, sample.width, `CHECK ${index + 1}`, track.palette.accent, track.palette.secondary);
    gate.name = `checkpoint-${index + 1}`;
    root.add(gate);
    return gate;
  });
  const boosterMaterials: THREE.MeshStandardMaterial[] = [];
  runtime.boosterProgresses.forEach((progress) => {
    const booster = createBooster(runtime.sampleAt(progress), track);
    root.add(booster.group);
    boosterMaterials.push(booster.material);
  });
  const { start, finish } = createStartFinish(runtime, track);
  root.add(start, finish);

  const arrowCount = Math.floor(runtime.totalLength / 95);
  for (let index = 1; index <= arrowCount; index += 1) {
    const progress = Math.min(0.96, index / (arrowCount + 1));
    root.add(createArrowSign(runtime.sampleAt(progress), track, index % 2 === 0 ? -1 : 1));
  }

  return {
    root,
    roadPoints: points,
    boosterMaterials,
    checkpointGroups,
    finishGroup: finish,
    update(elapsed) {
      boosterMaterials.forEach((entry, index) => {
        entry.emissiveIntensity = 3.4 + Math.sin(elapsed * 7 + index * 1.3) * 1.35;
      });
      checkpointGroups.forEach((gate, index) => {
        gate.scale.setScalar(1 + Math.sin(elapsed * 2.2 + index) * 0.006);
      });
    },
    dispose() {
      root.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((entry) => {
            if (entry instanceof THREE.MeshBasicMaterial && entry.map) entry.map.dispose();
            entry.dispose();
          });
        }
      });
      root.removeFromParent();
    },
  };
}
